import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useTranslation } from "react-i18next";
import type { BillingCycle, Plan } from "../../../api/billing";
import {
  RECOMMENDED_PLAN_SLUG,
  buildQuote,
  byDisplayOrder,
  formatMoney,
  saasPerVehicleCents,
  addonTotalCents,
} from "../pricing";

export interface PlanSelection {
  planSlug: string | null;
  addonSlug: string | null;
  cycle: BillingCycle;
  vehicleQuantity: number;
}

interface PlanSelectorProps {
  plans: Plan[];
  value: PlanSelection;
  onChange: (next: PlanSelection) => void;
  /** Slug of the plan the company is already on — renders a "Current" chip. */
  currentPlanSlug?: string | null;
  /** Hide the AI add-on section (e.g. a compact upgrade dialog). */
  hideAddons?: boolean;
  maxVehicles?: number;
  disabled?: boolean;
}

/**
 * Plan + cycle + vehicle-count picker.
 *
 * Shared deliberately between the signup wizard and the Billing page: the
 * price a prospect is quoted at signup and the price an existing customer sees
 * when upgrading are the same component reading the same `pricing.ts` maths,
 * so they cannot disagree. All money formatting goes through formatMoney().
 */
export const PlanSelector: React.FC<PlanSelectorProps> = ({
  plans,
  value,
  onChange,
  currentPlanSlug = null,
  hideAddons = false,
  maxVehicles = 500,
  disabled = false,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const saasPlans = useMemo(
    () => plans.filter((p) => p.tier === "saas" && p.is_active).sort(byDisplayOrder),
    [plans]
  );
  const addons = useMemo(
    () => plans.filter((p) => p.tier === "ai_addon" && p.is_active).sort(byDisplayOrder),
    [plans]
  );

  const selectedPlan = saasPlans.find((p) => p.slug === value.planSlug) ?? null;
  const selectedAddon = addons.find((p) => p.slug === value.addonSlug) ?? null;
  const currency = selectedPlan?.currency ?? saasPlans[0]?.currency ?? "EUR";

  const quote = buildQuote(selectedPlan, selectedAddon, value.cycle, value.vehicleQuantity);
  const money = (c: number) => formatMoney(c, currency, i18n.language);

  const patch = (p: Partial<PlanSelection>) => onChange({ ...value, ...p });

  return (
    <Stack spacing={3}>
      {/* ── Cycle + vehicle count ─────────────────────────────────────── */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            exclusive
            size="small"
            value={value.cycle}
            disabled={disabled}
            onChange={(_, v) => v && patch({ cycle: v as BillingCycle })}
          >
            <ToggleButton value="monthly">{t("billing.monthly", "Monthly")}</ToggleButton>
            <ToggleButton value="annual">{t("billing.annual", "Annual")}</ToggleButton>
          </ToggleButtonGroup>
          {/* Shown while on MONTHLY, not on annual. Monthly is now the cycle
              this opens on, and the chip's job is to advertise what switching
              would save — on the annual tab it was congratulating a choice the
              user had already made, and after the default flipped it would
              never have been seen at all. quote.annualSavingCents compares the
              two cycles over a full year, so it is valid on either tab. */}
          {value.cycle === "monthly" && quote.annualSavingCents > 0 && (
            <Chip
              size="small"
              color="success"
              label={t("billing.saveAmountPerYear", {
                defaultValue: "Save {{amount}}/year",
                amount: money(quote.annualSavingCents),
              })}
              sx={{ fontSize: 11 }}
            />
          )}
        </Stack>

        <Box sx={{ minWidth: { sm: 260 }, flexGrow: 1, maxWidth: 380 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography fontSize={13} fontWeight={600}>
              {t("billing.vehicleCount", "Number of vehicles")}
            </Typography>
            <Typography fontWeight={700} color="primary">
              {value.vehicleQuantity}
            </Typography>
          </Stack>
          <Slider
            size="small"
            min={1}
            max={maxVehicles}
            value={Math.min(value.vehicleQuantity, maxVehicles)}
            disabled={disabled}
            onChange={(_, v) => patch({ vehicleQuantity: v as number })}
            aria-label={t("billing.vehicleCount", "Number of vehicles")}
          />
        </Box>
      </Stack>

      {/* ── SaaS plans ────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          // Room for the chip that overhangs the recommended card's top edge.
          // Without it the chip is clipped by whatever sits above the grid,
          // which is the same bug in a different place.
          pt: 1.5,
        }}
      >
        {saasPlans.map((plan) => {
          const isSelected = plan.slug === value.planSlug;
          const isCurrent = plan.slug === currentPlanSlug;
          const isRecommended = plan.slug === RECOMMENDED_PLAN_SLUG;
          const perVehicle = saasPerVehicleCents(plan, value.cycle);
          const features: string[] = Array.isArray(plan.features) ? plan.features : [];

          return (
            <Card
              key={plan.slug}
              variant="outlined"
              onClick={() => !disabled && patch({ planSlug: plan.slug })}
              sx={{
                position: "relative",
                // The "Recommended" chip is deliberately hung above the card
                // edge (top: -11). MUI's Card sets overflow: hidden, which
                // sliced the chip in half — so overflow has to be reopened
                // here rather than moving the chip inside the border.
                overflow: "visible",
                cursor: disabled ? "default" : "pointer",
                borderWidth: 2,
                borderColor: isSelected ? "primary.main" : "divider",
                boxShadow: isSelected ? theme.shadows[4] : "none",
                transition: "border-color .15s, box-shadow .15s",
                "&:hover": disabled ? {} : { borderColor: "primary.light" },
              }}
              // Card is the click target, so it must also be keyboard-reachable.
              role="radio"
              aria-checked={isSelected}
              tabIndex={disabled ? -1 : 0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (!disabled) patch({ planSlug: plan.slug });
                }
              }}
            >
              {isRecommended && (
                <Chip
                  size="small"
                  color="primary"
                  label={t("billing.recommended", "Recommended")}
                  sx={{ position: "absolute", top: -11, left: 16, fontSize: 11 }}
                />
              )}
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography fontWeight={700}>{plan.name}</Typography>
                  {isCurrent && (
                    <Chip size="small" label={t("billing.currentBadge", "Current")} />
                  )}
                </Stack>

                <Stack direction="row" alignItems="baseline" spacing={0.5} mt={1}>
                  <Typography variant="h4" fontWeight={800}>
                    {money(perVehicle)}
                  </Typography>
                  <Typography fontSize={13} color="text.secondary">
                    {t("billing.perVehicleMonth", "/vehicle/month")}
                  </Typography>
                </Stack>

                <Typography fontSize={12} color="text.secondary">
                  {value.cycle === "annual"
                    ? t("billing.billedAnnually", "Billed annually")
                    : t("billing.billedMonthly", "Billed monthly")}
                </Typography>

                {features.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack spacing={0.75}>
                      {features.slice(0, 5).map((f) => (
                        <Stack key={f} direction="row" spacing={1} alignItems="flex-start">
                          <CheckRoundedIcon sx={{ fontSize: 16, mt: "2px" }} color="success" />
                          <Typography fontSize={12.5} color="text.secondary">
                            {/* Feature slugs are translated where a label
                                exists; otherwise the slug is de-snaked so a
                                newly added feature never renders as a blank. */}
                            {t(`billing.feature.${f}`, f.replace(/_/g, " "))}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* ── AI add-ons ────────────────────────────────────────────────── */}
      {!hideAddons && addons.length > 0 && (
        <Box>
          <Typography fontWeight={700} fontSize={14} mb={0.5}>
            {t("billing.aiLayer", "AI Layer")}
          </Typography>
          <Typography fontSize={12.5} color="text.secondary" mb={1.5}>
            {t("billing.addonOptional", "Optional add-on")}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            }}
          >
            {addons.map((addon) => {
              const isSelected = addon.slug === value.addonSlug;
              // Same source as the SaaS cards: the plan's own feature slugs.
              // These were already seeded on the add-on plans but never
              // rendered here, so all three cards showed a name and a price
              // and nothing to justify the difference between 133 and 716.
              const addonFeatures: string[] = Array.isArray(addon.features)
                ? addon.features
                : [];

              return (
                <Card
                  key={addon.slug}
                  variant="outlined"
                  // Clicking the selected add-on clears it: the AI layer is
                  // optional and there is no other affordance to opt out.
                  onClick={() =>
                    !disabled && patch({ addonSlug: isSelected ? null : addon.slug })
                  }
                  sx={{
                    cursor: disabled ? "default" : "pointer",
                    borderWidth: 2,
                    borderColor: isSelected ? "primary.main" : "divider",
                    // Equal-height cards: the three add-ons have different
                    // numbers of features, and without this the grid rows
                    // stagger and the prices stop lining up.
                    height: "100%",
                    "&:hover": disabled ? {} : { borderColor: "primary.light" },
                  }}
                  role="checkbox"
                  aria-checked={isSelected}
                  tabIndex={disabled ? -1 : 0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      if (!disabled) patch({ addonSlug: isSelected ? null : addon.slug });
                    }
                  }}
                >
                  <CardContent sx={{ py: 1.75 }}>
                    <Typography fontWeight={700} fontSize={14}>
                      {addon.name}
                    </Typography>
                    <Stack direction="row" alignItems="baseline" spacing={0.5} mt={0.5}>
                      <Typography variant="h6" fontWeight={800}>
                        {money(addonTotalCents(addon, value.cycle))}
                      </Typography>
                      <Typography fontSize={12} color="text.secondary">
                        {value.cycle === "annual"
                          ? t("billing.perYear", "/ year")
                          : t("billing.perMonth", "/ month")}
                      </Typography>
                    </Stack>

                    {addonFeatures.length > 0 && (
                      <>
                        <Divider sx={{ my: 1.25 }} />
                        <Stack spacing={0.6}>
                          {addonFeatures.map((f) => (
                            <Stack
                              key={f}
                              direction="row"
                              spacing={1}
                              alignItems="flex-start"
                            >
                              <CheckRoundedIcon
                                sx={{ fontSize: 15, mt: "2px" }}
                                color="success"
                              />
                              <Typography fontSize={12} color="text.secondary">
                                {/* Translated where a label exists, otherwise
                                    the slug is de-snaked so a feature added by
                                    an admin never renders as a blank line. */}
                                {t(`billing.feature.${f}`, f.replace(/_/g, " "))}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ── Running total ─────────────────────────────────────────────── */}
      {selectedPlan && (
        <Card variant="outlined" sx={{ bgcolor: "action.hover" }}>
          <CardContent>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography fontSize={13.5}>
                  {selectedPlan.name} — {value.vehicleQuantity}{" "}
                  {t("billing.vehicles", "Vehicles").toLowerCase()}
                </Typography>
                <Typography fontSize={13.5} fontWeight={600}>
                  {money(quote.saasTotalCents)}
                </Typography>
              </Stack>

              {selectedAddon && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontSize={13.5}>{selectedAddon.name}</Typography>
                  <Typography fontSize={13.5} fontWeight={600}>
                    {money(quote.addonTotalCents)}
                  </Typography>
                </Stack>
              )}

              <Divider />

              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography fontWeight={700}>
                  {value.cycle === "annual"
                    ? t("billing.totalPerYear", "Total per year")
                    : t("billing.totalPerMonth", "Total per month")}
                </Typography>
                <Typography variant="h5" fontWeight={800}>
                  {money(quote.totalCents)}
                </Typography>
              </Stack>

              {value.cycle === "annual" && (
                <Typography fontSize={12} color="text.secondary" textAlign="right">
                  ≈ {money(quote.monthlyEquivalentCents)} {t("billing.perMonth", "/ month")}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default PlanSelector;
